// Display zoom: 100% = the file's ORIGINAL size (1 content pixel = 1 CSS
// pixel; a PDF page at its natural dimensions). The initial value is whatever
// percentage fits the viewer, capped at 100. Display only — export always
// runs at natural size regardless.

import { useMemo, useState, type RefObject } from 'react';

/** The zoom-button stops (percent of original size). Typed values may land
 *  anywhere in 1–200; the buttons snap to the next stop past the current. */
const ZOOM_STOPS = [1, 10, 25, 50, 75, 100, 110, 120, 150, 200];

export function useDocumentZoom(viewerRef: RefObject<HTMLDivElement | null>) {
  const [zoom, setZoom] = useState<number | null>(null); // null until the content is measured
  // The content's natural pixel dimensions (current PDF page / the image) —
  // the aspect basis for sizing placed image stamps.
  const [contentNatural, setContentNatural] = useState<{ width: number; height: number } | null>(null);
  // The image's own pixels, for its explicit zoomed dimensions.
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

  /** Initializes zoom from the content's natural size, once. */
  const initZoom = (natural: { width: number; height: number } | undefined) => {
    if (natural) setContentNatural(natural);
    setZoom((current) => {
      if (current !== null) return current;
      const viewer = viewerRef.current;
      if (!natural || !viewer || natural.width <= 0 || viewer.clientWidth <= 0) return 100;
      const fit = Math.min(viewer.clientWidth / natural.width, viewer.clientHeight / natural.height, 1);
      return Math.max(1, Math.round(fit * 100));
    });
  };

  /** Steps to the next/previous stop past the current zoom. */
  const applyZoom = (direction: 1 | -1) => {
    setZoom((current) => {
      if (current === null) return current;
      const next = direction === 1
        ? ZOOM_STOPS.find((stop) => stop > current)
        : [...ZOOM_STOPS].reverse().find((stop) => stop < current);
      return next ?? current;
    });
  };

  /** Direct entry from the toolbar's zoom input, clamped to 1–200. */
  const setZoomPct = (pct: number) => {
    setZoom(Math.min(200, Math.max(1, Math.round(pct))));
  };

  /** The <img>'s onLoad — records natural size and fit-initializes zoom. */
  const handleImageLoad = (naturalWidth: number, naturalHeight: number) => {
    setNaturalSize(naturalWidth > 0 ? { w: naturalWidth, h: naturalHeight } : null);
    initZoom(naturalWidth > 0 ? { width: naturalWidth, height: naturalHeight } : undefined);
  };

  // Image zoom: explicit dimensions straight from natural size × zoom.
  const imageZoomStyle = useMemo(() => {
    if (zoom === null || !naturalSize) return undefined;
    return {
      width: (naturalSize.w * zoom) / 100,
      height: (naturalSize.h * zoom) / 100,
      maxWidth: 'none',
      maxHeight: 'none',
    };
  }, [zoom, naturalSize]);

  return { zoom, setZoomPct, applyZoom, initZoom, handleImageLoad, contentNatural, imageZoomStyle };
}
